import React from "react";
var __jsx = React.createElement;
import { createContext, useCallback, useContext, useRef } from "react";
import { trackEvents } from "@/common/events/events";
import { handleGtmEvent } from "@/common/events/gtmEvents";
import { createAbandonmentDetector, registerAbandonmentDetectorCleanup, stopAbandonmentDetector } from "@/utils/abandonmentDetector";
var AbandonmentTrackingContext = /*#__PURE__*/createContext(undefined);
export var AbandonmentTrackingProvider = function AbandonmentTrackingProvider(_ref) {
  var children = _ref.children;
  var trackersRef = useRef(new Map());
  var clearAbandonmentTracking = useCallback(function (key) {
    if (!key) return;
    stopAbandonmentDetector(key);
    trackersRef.current["delete"](key);
  }, []);
  var stopTracking = useCallback(function (key) {
    clearAbandonmentTracking(key);
  }, [clearAbandonmentTracking]);
  var completeAbandonmentTracking = useCallback(function (key) {
    var tracker = trackersRef.current.get(key);
    if (tracker) {
      tracker.completed = true;
    }
    clearAbandonmentTracking(key);
  }, [clearAbandonmentTracking]);
  var updateAbandonmentTracking = useCallback(function (key, input) {
    var tracker = trackersRef.current.get(key);
    if (!tracker) return;
    if (input.event) {
      tracker.event = input.event;
    }
    if (input.payload) {
      tracker.payload = input.payload;
    }
    if (typeof input.shouldTrack === "boolean") {
      tracker.shouldTrack = input.shouldTrack;
    }
  }, []);
  var startAbandonmentTracking = useCallback(function (input) {
    if (!__IS_CSR__) return;
    if (!input.key) return;
    var existingTracker = trackersRef.current.get(input.key);
    if (existingTracker) {
      updateAbandonmentTracking(input.key, {
        event: input.event,
        payload: input.payload || {},
        shouldTrack: input.shouldTrack
      });
      return;
    }
    var detector = createAbandonmentDetector(function (abandonmentState) {
      var currentTracker = trackersRef.current.get(input.key);
      if (!currentTracker) return;
      if (currentTracker.completed) return;
      if (!currentTracker.shouldTrack) return;
      if (abandonmentState !== null && abandonmentState !== void 0 && abandonmentState.didExitPage) {
        handleGtmEvent(currentTracker.event, currentTracker.payload || {});
      } else {
        trackEvents(currentTracker.event, currentTracker.payload || {});
      }
      currentTracker.completed = true;
      clearAbandonmentTracking(input.key);
    });
    var cleanup = detector.start();
    var tracker = {
      key: input.key,
      event: input.event,
      payload: input.payload || {},
      shouldTrack: Boolean(input.shouldTrack),
      cleanup: cleanup,
      completed: false
    };
    trackersRef.current.set(input.key, tracker);
    registerAbandonmentDetectorCleanup(input.key, function () {
      tracker.completed = true;
      tracker.cleanup();
      trackersRef.current["delete"](input.key);
    });
  }, [clearAbandonmentTracking, updateAbandonmentTracking]);
  return /*#__PURE__*/React.createElement(AbandonmentTrackingContext.Provider, {
    value: {
      startAbandonmentTracking: startAbandonmentTracking,
      updateAbandonmentTracking: updateAbandonmentTracking,
      stopAbandonmentDetector: stopTracking,
      completeAbandonmentTracking: completeAbandonmentTracking,
      clearAbandonmentTracking: clearAbandonmentTracking
    }
  }, children);
};
export var useAbandonmentTracking = function useAbandonmentTracking() {
  var context = useContext(AbandonmentTrackingContext);
  if (!context) {
    throw new Error("useAbandonmentTracking must be used within AbandonmentTrackingProvider");
  }
  return context;
};
export default AbandonmentTrackingProvider;